<template>
  <div class="sales-assistant">
    <!-- هدر -->
    <CCard class="mb-4">
      <CRow class="align-items-center">
        <CCol md="6">
          <h3 class="mb-1">🎯 دستیار فروش هوشمند</h3>
          <p class="text-muted mb-0">توصیه‌های عملی بر اساس داده‌های واقعی</p>
        </CCol>
        <CCol md="6" class="text-left">
          <CButton color="primary" @click="loadInsights" :disabled="loading">
            <i class="cil-reload"></i> بروزرسانی
          </CButton>
          <CBadge color="light" class="mr-2">
            <i class="cil-clock"></i> {{ lastUpdate }}
          </CBadge>
        </CCol>
      </CRow>
    </CCard>

    <!-- کارت‌های خلاصه -->
    <CRow class="mb-4">
      <CCol md="3" v-for="stat in stats" :key="stat.title">
        <CCard :class="`text-center text-white bg-${stat.variant}`">
          <h4 class="mb-1">{{ stat.value }}</h4>
          <small>{{ stat.title }}</small>
        </CCard>
      </CCol>
    </CRow>

    <!-- لیست توصیه‌ها -->
    <CCard v-for="(recommendation, index) in recommendations" :key="index"
            class="mb-3 recommendation-card"
            :class="`border-${recommendation.color}`">

      <CRow class="align-items-center">
        <CCol md="1" class="text-center">
          <i :class="`cil ${recommendation.icon} text-${recommendation.color}`"
             style="font-size: 1.5rem;"></i>
        </CCol>

        <CCol md="7">
          <h6 class="mb-1">
            <CBadge :color="recommendation.color" class="mr-2">
              {{ getPriorityText(recommendation.priority) }}
            </CBadge>
            {{ recommendation.title }}
          </h6>
          <p class="text-muted mb-1 small">{{ recommendation.description }}</p>
          <p class="mb-0">
            <strong>اقدام پیشنهادی:</strong> {{ recommendation.action }}
          </p>
        </CCol>

        <CCol md="2" class="text-center">
          <CBadge color="light">
            {{ recommendation.count }} مورد
          </CBadge>
        </CCol>

        <CCol md="2" class="text-left">
          <CButton color="outline-primary" size="sm" @click="showDetails(recommendation)">
            <i class="cil-list-rich"></i> جزئیات
          </CButton>
        </CCol>
      </CRow>
    </CCard>

    <!-- حالت‌های مختلف -->
    <CCard v-if="loading" class="text-center">
      <CSpinner color="primary"></CSpinner>
      <p class="mt-2">در حال تحلیل داده‌ها...</p>
    </CCard>

    <CCard v-if="!loading && recommendations.length === 0" class="text-center">
      <i class="cil-check-circle text-success" style="font-size: 3rem;"></i>
      <h5 class="mt-3">همه چیز تحت کنترل است!</h5>
      <p class="text-muted">هیچ اقدام فوری نیاز نیست</p>
    </CCard>

    <!-- مودال جزئیات -->
    <CModal :show.sync="showDetailModal" title="توصیه" size="lg">
      <div v-if="selectedRecommendation">
        <CAlert :color="selectedRecommendation.color" show>
          <strong>اولویت:</strong> {{ getPriorityText(selectedRecommendation.priority) }} |
          <strong>تعداد موارد:</strong> {{ selectedRecommendation.count }}
        </CAlert>

        <p>{{ selectedRecommendation.description }}</p>

        <h6>اقدام پیشنهادی:</h6>
        <CCard class="bg-light">
          {{ selectedRecommendation.action }}
        </CCard>

        <h6 class="mt-3">موارد مرتبط:</h6>
        <CListGroup>
          <CListGroupItem v-for="(item, idx) in selectedRecommendation.data" :key="idx">
            <div v-if="selectedRecommendation.type === 'customer_retention'">
              <strong>{{ item.name }}</strong> -
              {{ item.phone }} -
              {{ formatCurrency(item.total_spent) }}
            </div>

            <div v-else-if="selectedRecommendation.type === 'inventory_management'">
              <strong>{{ item.title }}</strong> -
              موجودی: {{ item.current_stock }} -
              وضعیت: {{ getStockStatusText(item.stock_status) }}
            </div>

            <div v-else-if="selectedRecommendation.type === 'product_optimization'">
              <strong>{{ item.title }}</strong> -
              نرخ تبدیل: {{ item.conversion_rate }}% -
              بازدید: {{ item.total_views }}
            </div>

            <div v-else-if="selectedRecommendation.type === 'sales_opportunity'">
              <strong>{{ item.title }}</strong> -
              پتانسیل: {{ getPotentialText(item.potential_status) }} -
              بازدید: {{ item.total_views }}
            </div>
          </CListGroupItem>
        </CListGroup>
      </div>
    </CModal>
  </div>
</template>

<script>
import axios from "axios";
export default {
  name: 'SaleAssistant',
  data() {
    return {
      recommendations: [],
      summary: {},
      loading: false,
      lastUpdate: '',
      showDetailModal: false,
      selectedRecommendation: null
    };
  },

  computed: {
    stats() {
      return [
        {
          title: 'توصیه‌های فوری',
          value: this.summary.critical_count || 0,
          variant: 'danger'
        },
        {
          title: 'اولویت بالا',
          value: this.summary.high_count || 0,
          variant: 'warning'
        },
        {
          title: 'توصیه‌ها',
          value: this.summary.total_recommendations || 0,
          variant: 'info'
        },
        {
          title: 'موارد کل',
          value: this.summary.total_items || 0,
          variant: 'success'
        }
      ];
    }
  },

  mounted() {
    this.loadInsights();
  },

  methods: {
    async loadInsights() {
      this.loading = true;
      try {
        const response = await axios.get('/api/admin/sale-assistant/insights');
        if (response.data.success) {
          this.recommendations = response.data.data.recommendations;
          this.summary = response.data.data.summary;
          this.lastUpdate = this.formatTime(response.data.data.last_updated);
        }
      } catch (error) {
        console.error('Error loading insights:', error);
        this.$notify.error('خطا در دریافت داده‌ها');
      }
      this.loading = false;
    },

    getPriorityText(priority) {
      const texts = {
        'critical': 'فوری',
        'high': 'بالا',
        'medium': 'متوسط'
      };
      return texts[priority] || priority;
    },

    getStockStatusText(status) {
      const texts = {
        'CRITICAL': 'بحرانی',
        'LOW': 'کم',
        'NORMAL': 'عادی'
      };
      return texts[status] || status;
    },

    getPotentialText(potential) {
      const texts = {
        'HIGH_POTENTIAL': 'عالی',
        'MEDIUM_POTENTIAL': 'خوب',
        'NORMAL': 'معمولی'
      };
      return texts[potential] || potential;
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('fa-IR').format(value) + ' تومان';
    },

    formatTime(timestamp) {
      return new Date(timestamp).toLocaleString('fa-IR');
    },

    showDetails(recommendation) {
      this.selectedRecommendation = recommendation;
      this.showDetailModal = true;
    }
  }
};
</script>

<style scoped>
.sales-assistant {
  padding: 20px;
}

.recommendation-card {
  border-left: 4px solid;
  transition: all 0.3s ease;
}

.recommendation-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.border-danger { border-left-color: #dc3545 !important; }
.border-warning { border-left-color: #ffc107 !important; }
.border-info { border-left-color: #17a2b8 !important; }
.border-success { border-left-color: #28a745 !important; }

.text-danger { color: #dc3545 !important; }
.text-warning { color: #ffc107 !important; }
.text-info { color: #17a2b8 !important; }
.text-success { color: #28a745 !important; }
</style>